/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
import { __decorate } from "tslib";
import { html, css, LitElement } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { unreacheable } from '../../internal/unreacheable.js';
/**
 * WebComponent.
 */
let PwcTooltip = class PwcTooltip extends LitElement {
    constructor() {
        super(...arguments);
        this._visibility = '';
        // space between the tooltip and the element.
        this._offset = 8;
        this._showToolTip = (event) => {
            // only show tooltip when using a mouse and not in mobile (touch)
            if (event.pointerType === 'mouse') {
                this._visibility = 'visible';
            }
        };
        this._hideTooltip = () => {
            this._visibility = '';
        };
    }
    /**
     * @inheritdoc
     */
    connectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.connectedCallback();
        // guard.
        if (!this.parentElement) {
            throw new Error('Illegal state: no parent element.');
        }
        this.parentElement.addEventListener('pointerenter', this._showToolTip);
        this.parentElement.addEventListener('pointerleave', this._hideTooltip);
    }
    disconnectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.disconnectedCallback();
        // guard
        if (!this.parentElement) {
            return;
        }
        this.parentElement.removeEventListener('pointerenter', this._showToolTip);
        this.parentElement.removeEventListener('pointerleave', this._hideTooltip);
    }
    /**
     * @inheritdoc
     */
    render() {
        return html `
      <div class="tooltip ${this._visibility}">
        <div class="tooltiptext"><slot></slot></div>
      </div>
    `;
    }
    /**
     * @inheritdoc
     */
    updated(_changedProperties) {
        super.updated(_changedProperties);
        // guard.
        if (this._visibility === '') {
            return;
        }
        this._updatePosition();
    }
    /**
     * Update the position of the tooltip text.
     */
    _updatePosition() {
        const positions = [
            'top',
            'right',
            'bottom',
            'left',
            'top left',
            'top right',
            'bottom left',
            'bottom right',
        ];
        for (const position of positions) {
            this._setTooltipPosition(position);
            if (this._isTooltipTextInViewport()) {
                return;
            }
        }
    }
    /**
     * Set the tooltip position.
     */
    _setTooltipPosition(position) {
        // guard.
        if (!this.parentElement) {
            return;
        }
        // get parent position.
        const parentClientRects = this.parentElement.getBoundingClientRect();
        let top = 0;
        let left = 0;
        // top.
        switch (position) {
            case 'top':
            case 'top left':
            case 'top right':
                top = parentClientRects.top - this._offset;
                break;
            case 'left':
            case 'right':
                top = parentClientRects.top + parentClientRects.height / 2;
                break;
            case 'bottom':
            case 'bottom left':
            case 'bottom right':
                top = parentClientRects.bottom + this._offset;
                break;
            default:
                unreacheable(position);
        }
        // left.
        switch (position) {
            case 'top':
            case 'bottom':
                left = parentClientRects.left + parentClientRects.width / 2;
                break;
            case 'right':
            case 'top right':
            case 'bottom right':
                left = parentClientRects.right + this._offset;
                break;
            case 'left':
            case 'top left':
            case 'bottom left':
                left = parentClientRects.left - this._offset;
                break;
            default:
                unreacheable(position);
        }
        this._tooltipElement.className = `tooltip visible ${position}`;
        this._tooltipElement.style.top = `${top}px`;
        this._tooltipElement.style.left = `${left}px`;
    }
    /**
     * Check that the tooltip text is in the viewport.
     */
    _isTooltipTextInViewport() {
        const rect = this._tooltipTextElement.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;
        return (rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= windowHeight &&
            rect.right <= windowWidth);
    }
};
// styles.
PwcTooltip.styles = css `
    :host {
      position: relative;

      /* sizes */
      --pwc-tooltip-arrow-size: 5px;
      --pwc-tooltip-line-height: 1rem;
      --pwc-tooltip-font-size-small: var(--pwc-font-size-small, 0.875rem);

      /* z-index */
      --pwc-tooltip-z-index: var(--pwc-z-index-tooltip, 1000);
    }

    /* Anchor of the tooltip */
    .tooltip {
      position: fixed;
      display: inline-block;
      z-index: var(--pwc-tooltip-z-index);
      pointer-events: none;
    }

    /* Tooltip */
    .tooltip .tooltiptext {
      opacity: 0;
      visibility: hidden;
      background-color: black;
      color: #fff;
      text-align: center;
      border-radius: 0.25rem;
      position: absolute;
      white-space: nowrap;
      padding: 7px 11px;
      font-size: var(--pwc-tooltip-font-size-small);
      line-height: var(--pwc-tooltip-line-height);
    }

    /* Tooltip Position */
    .tooltip.top .tooltiptext {
      transform: translate(-50%, -100%);
    }

    .tooltip.bottom .tooltiptext {
      transform: translate(-50%, 0%);
    }

    .tooltip.left .tooltiptext {
      transform: translate(-100%, -50%);
    }

    .tooltip.right .tooltiptext {
      transform: translate(0%, -50%);
    }

    .tooltip.top.left .tooltiptext {
      transform: translate(calc(-100% + 30px), -100%);
    }

    .tooltip.bottom.left .tooltiptext {
      transform: translate(calc(-100% + 30px), 0%);
    }

    .tooltip.top.right .tooltiptext {
      transform: translate(-30px, -100%);
    }

    .tooltip.bottom.right .tooltiptext {
      transform: translate(-30px, 0%);
    }

    /* Tooltip Arrow */
    .tooltip .tooltiptext::after {
      content: '';
      position: absolute;
      border-style: solid;
      border-width: var(--pwc-tooltip-arrow-size);
    }

    /* Tooltip Arrow Position */
    .tooltip.top .tooltiptext::after {
      left: 50%;
      margin-left: calc(var(--pwc-tooltip-arrow-size) * -1);
    }

    .tooltip.bottom .tooltiptext::after {
      left: 50%;
      margin-left: calc(var(--pwc-tooltip-arrow-size) * -1);
    }

    .tooltip.left .tooltiptext::after {
      left: 100%;
      margin-right: calc(var(--pwc-tooltip-arrow-size) * -2);
    }

    .tooltip.right .tooltiptext::after {
      left: 0%;
      margin-left: calc(var(--pwc-tooltip-arrow-size) * -2);
    }

    .tooltip.top.left .tooltiptext::after,
    .tooltip.bottom.left .tooltiptext::after {
      left: calc(100% - 15px);
    }

    .tooltip.top.right .tooltiptext::after,
    .tooltip.bottom.right .tooltiptext::after {
      left: 15px;
    }

    /* Tooltip Top Position */
    .tooltip.left .tooltiptext::after {
      top: 50%;
      margin-top: calc(var(--pwc-tooltip-arrow-size) * -1);
    }

    .tooltip.right .tooltiptext::after {
      top: 50%;
      margin-top: calc(var(--pwc-tooltip-arrow-size) * -1);
    }

    .tooltip.top .tooltiptext::after,
    .tooltip.top.left .tooltiptext::after,
    .tooltip.top.right .tooltiptext::after {
      top: 100%;
      margin-top: 0;
    }

    .tooltip.bottom .tooltiptext::after,
    .tooltip.bottom.left .tooltiptext::after,
    .tooltip.bottom.right .tooltiptext::after {
      top: 0%;
      margin-top: calc(var(--pwc-tooltip-arrow-size) * -2);
    }

    /* Tooltip Arrow Shape */
    .tooltip.left .tooltiptext::after {
      border-color: transparent transparent transparent black;
    }

    .tooltip.right .tooltiptext::after {
      border-color: transparent black transparent transparent;
    }

    .tooltip.top .tooltiptext::after,
    .tooltip.top.left .tooltiptext::after,
    .tooltip.top.right .tooltiptext::after {
      border-color: black transparent transparent transparent;
    }

    .tooltip.bottom .tooltiptext::after,
    .tooltip.bottom.left .tooltiptext::after,
    .tooltip.bottom.right .tooltiptext::after {
      border-color: transparent transparent black transparent;
    }

    /* Tooltip Display */
    .tooltip.visible .tooltiptext {
      opacity: 1;
      visibility: visible;
    }

    .tooltip .tooltiptext {
      transition: opacity 0.1s ease-in 500ms;
    }
  `;
__decorate([
    state()
], PwcTooltip.prototype, "_visibility", void 0);
__decorate([
    query('.tooltiptext', true)
], PwcTooltip.prototype, "_tooltipTextElement", void 0);
__decorate([
    query('.tooltip', true)
], PwcTooltip.prototype, "_tooltipElement", void 0);
PwcTooltip = __decorate([
    customElement('pwc-tooltip')
], PwcTooltip);
export { PwcTooltip };
//# sourceMappingURL=pwc-tooltip.js.map